/**
 * Browser Geolocation (ADR-0008 "use my location"). Best-effort, never a dead end: a denial or
 * unsupported device falls back to the address field (intake/spec §4). No map, no provider.
 *
 * On success, coords are reverse-geocoded (GET /api/public/reverse-geocode) and — when a label
 * comes back — handed to the caller via `onAddress` so it can fill the address field. A geocode
 * miss/failure is NOT a GPS error: coords are still attached ("success" status) and the caller
 * keeps the current behaviour (resident types the address).
 */
import { useCallback, useState } from "react";
import { reverseGeocode } from "../api/index.js";

export type GeoStatus =
  | "idle"
  | "requesting"
  | "geocoding"
  | "success"
  | "denied"
  | "error"
  | "unsupported";

export interface GeoResult {
  lat: number;
  lng: number;
  /** Accuracy radius in metres, shown as "accurate to ~Xm". */
  accuracy: number;
}

export interface UseGeolocation {
  status: GeoStatus;
  result: GeoResult | null;
  supported: boolean;
  /** True while GPS or the geocode lookup is in flight — drives the "Locating…" button state. */
  busy: boolean;
  /** Set when GPS succeeded but reverse-geocoding found no address — non-blocking note. */
  geocodeFailed: boolean;
  request: () => void;
  clear: () => void;
}

interface Options {
  /** Called with the resolved address label when reverse-geocoding finds one. */
  onAddress?: (address: string) => void;
}

export function useGeolocation(options: Options = {}): UseGeolocation {
  const { onAddress } = options;
  const supported = typeof navigator !== "undefined" && "geolocation" in navigator;
  const [status, setStatus] = useState<GeoStatus>(supported ? "idle" : "unsupported");
  const [result, setResult] = useState<GeoResult | null>(null);
  const [geocodeFailed, setGeocodeFailed] = useState(false);

  const request = useCallback(() => {
    if (!supported) {
      setStatus("unsupported");
      return;
    }
    setStatus("requesting");
    setGeocodeFailed(false);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const coords: GeoResult = {
          lat: round(pos.coords.latitude),
          lng: round(pos.coords.longitude),
          accuracy: Math.round(pos.coords.accuracy),
        };
        setResult(coords);
        setStatus("geocoding");
        try {
          const address = await reverseGeocode(coords.lat, coords.lng);
          if (address) {
            onAddress?.(address);
          } else {
            setGeocodeFailed(true);
          }
        } catch {
          // Geocode failure is never a dead end — coords stay attached, resident types the address.
          setGeocodeFailed(true);
        }
        setStatus("success");
      },
      (err) => {
        setStatus(err.code === err.PERMISSION_DENIED ? "denied" : "error");
      },
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 60_000 },
    );
  }, [supported, onAddress]);

  const clear = useCallback(() => {
    setResult(null);
    setGeocodeFailed(false);
    setStatus(supported ? "idle" : "unsupported");
  }, [supported]);

  const busy = status === "requesting" || status === "geocoding";

  return { status, result, supported, busy, geocodeFailed, request, clear };
}

/** ~6 decimal places ≈ 0.1m — plenty; avoids over-precise PII noise. */
function round(n: number): number {
  return Math.round(n * 1e6) / 1e6;
}
